import { getLocalTimeZone, today } from '@internationalized/date';
import { useControlledState } from '@react-stately/utils';
import { Calendar as CalendarIcon } from '@untitledui/icons';
import { useDateFormatter } from 'react-aria';
import {
  DatePicker as AriaDatePicker,
  Dialog as AriaDialog,
  Group as AriaGroup,
  Popover as AriaPopover
} from 'react-aria-components';
import type {
  DatePickerProps as AriaDatePickerProps,
  DateValue
} from 'react-aria-components';
import { Button } from '../../base/buttons/button';
import { cx } from '../../../utils/cx';
import { Calendar } from './calendar';
import { forwardRef } from 'react';

const highlightedDates = [today(getLocalTimeZone())];

export interface DatePickerProps extends AriaDatePickerProps<DateValue> {
  /** Placeholder text shown when no date is selected. */
  placeholder?: string;
  /** Hides the footer actions and closes on selection when set to false. */
  showActions?: boolean;
  /** Invoked when the apply button is clicked. */
  onApply?: () => void;
  /** Invoked when the cancel button is clicked. */
  onCancel?: () => void;
  /** Optional container for the popover portal. */
  popoverContainer?: HTMLElement | null;
  /** Stretches the trigger button to fill the available width. */
  fullWidth?: boolean;
  /** The label for the preset button. */
  presetButtonLabel?: string;
}

export const DatePicker = forwardRef<HTMLButtonElement, DatePickerProps>(
  (
    {
      value: valueProp,
      defaultValue,
      onChange,
      onApply,
      onCancel,
      placeholder = 'Select date',
      showActions = true,
      shouldCloseOnSelect,
      popoverContainer,
      fullWidth = false,
      className,
      presetButtonLabel,
      ...props
    }: DatePickerProps,
    ref
  ) => {
    const formatter = useDateFormatter({
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
    const [value, setValue] = useControlledState(
      valueProp,
      defaultValue || null,
      onChange
    );

    const formattedDate = value
      ? formatter.format(value.toDate(getLocalTimeZone()))
      : placeholder;
    const resolvedShouldClose = shouldCloseOnSelect ?? !showActions;

    type DatePickerClassNameFn = Exclude<
      DatePickerProps['className'],
      string | undefined | null | false
    >;
    type DatePickerClassNameProps = DatePickerClassNameFn extends (
      arg: infer P
    ) => string
      ? P
      : never;

    const resolvedDatePickerClassName: DatePickerProps['className'] =
      typeof className === 'function'
        ? (values: DatePickerClassNameProps) =>
            cx(
              fullWidth && 'flex w-full',
              'relative',
              (className as DatePickerClassNameFn)(values)
            )
        : cx(fullWidth && 'flex w-full', 'relative', className);

    return (
      <AriaDatePicker
        {...props}
        value={value}
        onChange={setValue}
        shouldCloseOnSelect={resolvedShouldClose}
        className={resolvedDatePickerClassName}
      >
        <AriaGroup className={cx(fullWidth && 'w-full')}>
          <Button
            size="md"
            color="secondary"
            iconLeading={CalendarIcon}
            className={cx(fullWidth && 'w-full justify-start pl-3! gap-1!')}
            ref={ref}
          >
            {formattedDate}
          </Button>
        </AriaGroup>
        <AriaPopover
          offset={8}
          placement="bottom right"
          UNSTABLE_portalContainer={popoverContainer || undefined}
          className={({ isEntering, isExiting }) =>
            cx(
              'origin-(--trigger-anchor-point) will-change-transform',
              isEntering &&
                'duration-150 ease-out animate-in fade-in placement-right:slide-in-from-left-0.5 placement-top:slide-in-from-bottom-0.5 placement-bottom:slide-in-from-top-0.5',
              isExiting &&
                'duration-100 ease-in animate-out fade-out placement-right:slide-out-to-left-0.5 placement-top:slide-out-to-bottom-0.5 placement-bottom:slide-out-to-top-0.5'
            )
          }
        >
          <AriaDialog className="rounded-2xl bg-primary shadow-xl ring ring-secondary_alt">
            {({ close }) => (
              <>
                <div className="flex px-6 py-5">
                  <Calendar
                    highlightedDates={highlightedDates}
                    {...(presetButtonLabel && { presetButtonLabel })}
                  />
                </div>
                {showActions !== false && (
                  <div className="grid grid-cols-2 gap-3 border-t border-secondary p-4">
                    <Button
                      size="md"
                      color="secondary"
                      onClick={() => {
                        onCancel?.();
                        close();
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="md"
                      color="primary"
                      onClick={() => {
                        onApply?.();
                        close();
                      }}
                    >
                      Apply
                    </Button>
                  </div>
                )}
              </>
            )}
          </AriaDialog>
        </AriaPopover>
      </AriaDatePicker>
    );
  }
);

DatePicker.displayName = 'DatePicker';
